import { Capacitor } from "@capacitor/core";
import Email from "@mui/icons-material/EmailOutlined";
import HelpOutlined from "@mui/icons-material/HelpOutlined";
import Lock from "@mui/icons-material/LockOutlined";
import Person from "@mui/icons-material/PersonOutlineOutlined";
import Phone from "@mui/icons-material/PhoneOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AxiosError } from "axios";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { useNavigate } from "react-router-dom";
import { AppleOAuthLoginButton } from "../../components/sso/buttons/AppleOAuthLoginButton";
import { BiometricLoginButton } from "../../components/sso/buttons/BiometricLoginButton";
import { GoogleOAuthLoginButton } from "../../components/sso/buttons/GoogleOAuthLoginButton";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { biometricService } from "../../services/biometric.service";
import { localStorageService } from "../../services/LocalStorageService";
import { INK, STATUS_BADGE } from "../../theme/tokens";
import { SUPPORT_EMAIL } from "../../utils/models";
import { classifyUsernameInput, formatUsernameInput } from "../../utils/phoneAndEmailUtils";
import { AUTH_EVENT, AUTH_METHOD, authEventProps } from "./authAnalytics";
import { SigninLayout, SubmitButton } from "./common";
import { useResendCooldown } from "./hooks/useResendCooldown";
import { isSupportOtpBlocked, useCheckUsernameStatus, useResetPasswordFromApp, useSendOtp, useSendResetPassword } from "./hooks/useSignInMutations";
import { LockedStep, OtpStep } from "./OtpStep";
import { LoginSeparator } from "./styled";

const BIOMETRIC_TIMEOUT_MS = 3000; // 3 seconds max for biometric
const OTP_RESEND_COOLDOWN_SECONDS = 30; // "Send again" is disabled for this long after each send

type SignInStep = "username" | "password" | "set-password" | "otp" | "locked";
type SignInMode = "otp" | "password";

export const SignIn: FC = () => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { login } = useAuth();
	const navigate = useNavigate();

	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
	const [isBiometricLoading, setIsBiometricLoading] = useState(Capacitor.isNativePlatform());
	const [isFormLoading, setIsFormLoading] = useState(false);
	const [username, setUsername] = useState("");
	const [step, setStep] = useState<SignInStep>("username");
	// OTP is the default (left) toggle option; determines what Continue does on the username step.
	const [mode, setMode] = useState<SignInMode>("otp");

	const checkUsernameStatusMutation = useCheckUsernameStatus();
	const sendResetPasswordMutation = useSendResetPassword();
	const resetPasswordFromAppMutation = useResetPasswordFromApp();
	const sendOtpMutation = useSendOtp();

	// "Send again" is throttled: each send starts a 30s cooldown during which the button is disabled.
	const { remaining: resendCooldown, start: startResendCooldown } = useResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
	const sendOtp = useCallback(
		(rawUsername: string) => {
			const formattedUsername = rawUsername.includes("@") ? rawUsername.trim().toLowerCase() : rawUsername.trim();
			startResendCooldown();
			return sendOtpMutation.mutateAsync(formattedUsername);
		},
		[sendOtpMutation, startResendCooldown],
	);

	// Set timeout for biometric loading to prevent indefinite wait
	useEffect(() => {
		if (Capacitor.isNativePlatform()) {
			const timeout = setTimeout(() => {
				setIsBiometricLoading(false);
			}, BIOMETRIC_TIMEOUT_MS);

			return () => clearTimeout(timeout);
		}
	}, []);

	const handleBiometricLoadingChange = useCallback((loading: boolean) => {
		setIsBiometricLoading(loading);
	}, []);

	const handleBiometricComplete = useCallback(() => {
		setIsBiometricLoading(false);
	}, []);

	const onUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const formattedValue = formatUsernameInput(e.target.value);
		setUsername(formattedValue);
	}, []);

	const handleUsernameContinue = useCallback(
		async (e: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
			e.preventDefault();

			const trimmedUsername = username.trim();
			if (!trimmedUsername) {
				showSnackbar(t("error.enter.credentials"), "warning");
				return;
			}

			const formattedUsername = trimmedUsername.includes("@") ? trimmedUsername.toLowerCase() : trimmedUsername;

			// The password path: reveal password entry, password setup, or send a reset email.
			const proceedWithPassword = async (result: Awaited<ReturnType<typeof checkUsernameStatusMutation.mutateAsync>>) => {
				if (result.is_initialized) {
					setStep("password");
				} else if (result.has_valid_pending_setup) {
					setStep("set-password");
				} else {
					await sendResetPasswordMutation.mutateAsync(formattedUsername);
				}
			};

			setIsFormLoading(true);
			try {
				const result = await checkUsernameStatusMutation.mutateAsync(formattedUsername);

				if (!result.user_exists) {
					// Error already handled in the mutation
					return;
				}

				if (mode === "otp") {
					try {
						// OTP mode: user confirmed to exist — send a one-time code and reveal the OTP step.
						await sendOtp(formattedUsername);
						setStep("otp");
					} catch (error) {
						// Support users can't use OTP — fall through to the password flow, as if Continue
						// had been pressed on the password tab. Other errors are handled in the mutation.
						if (isSupportOtpBlocked(error)) {
							await proceedWithPassword(result);
						}
					}
					return;
				}

				await proceedWithPassword(result);
			} catch {
				// Errors are already handled in the mutations
			} finally {
				setIsFormLoading(false);
			}
		},
		[username, showSnackbar, t, mode, checkUsernameStatusMutation, sendResetPasswordMutation, sendOtp],
	);

	const handleLogin = useCallback(
		async (e: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
			e.preventDefault();

			// Prevent form submission while biometric is in progress
			if (isBiometricLoading) {
				track("login attempt blocked", { reason: "biometric in progress" });
				return;
			}

			const formData = new FormData(e.currentTarget);
			const rawUsername = username.trim();
			const password = formData.get("password")?.toString();

			if (!rawUsername || !password) {
				showSnackbar(t("error.enter.credentials"), "warning");
				trackError("login", {
					type: "password",
					email: rawUsername,
					error: "missing email/password",
				});
				return;
			}

			// If it's an email, lowercase it; if it's a phone number, keep the formatting
			const formattedUsername = rawUsername.includes("@") ? rawUsername.toLowerCase() : rawUsername;

			setIsFormLoading(true);

			try {
				track("login", { type: "password", status: "start", email: formattedUsername });
				const result = await login(formattedUsername, password);

				if (Capacitor.isNativePlatform() || !result.mfa) {
					if (!result.isSuccessful) {
						trackError("login", {
							type: "password",
							status: "fail",
							email: formattedUsername,
							message: "wrong credentials",
						});
						trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.PASSWORD, false), reason: "wrong credentials" });
						return;
					}
					track("login", {
						type: "password",
						status: "success",
						email: formattedUsername,
					});
					track(AUTH_EVENT.LOGIN_SUCCESS, authEventProps(AUTH_METHOD.PASSWORD, true));
					// Handle biometric enrollment on native platforms
					if (Capacitor.isNativePlatform()) {
						try {
							const info = await biometricService.getPlatformInfo();
							localStorageService.setBiometricType(info.biometricType || "");
							localStorageService.setBiometricAvailable(info.biometricAvailable);

							// Prompt to enable biometric login
							if (info.biometricAvailable && !localStorageService.getDeclineBiometric()) {
								const enableBiometric = window.confirm("Enable biometric login for next time?");

								if (enableBiometric) {
									const saved = await biometricService.saveCredentials({
										email: formattedUsername,
										password,
									});

									if (saved) {
										localStorageService.setSavedCredentials(true);
										track("biometric login enabled", {
											email: formattedUsername,
										});
										showSnackbar("Biometric login enabled!", "success");
									} else {
										trackError("biometric credentials save failed", {
											email: formattedUsername,
											reason: "storage unavailable",
										});
									}
								} else {
									localStorageService.setDeclineBiometric(true);
								}
							}
						} catch (error) {
							trackError("check biometric availability failed", {
								error: (error as Error).message,
							});
							showSnackbar(`Biometric check failed: ${(error as Error).message}`, "error");
						}
					}
				}

				if (result.mfa) {
					navigate("/mfa", { state: { username: formattedUsername } });
					return;
				}

				navigate("/");
			} catch (error) {
				if (error instanceof AxiosError && !error.response) {
					showSnackbar(t("error.no.internet.connection"), "error");
					trackError("login", { action: "login failed", reason: "network error", username: formattedUsername });
					trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.PASSWORD, false), reason: "network error" });
					return;
				}

				let message = t("error.could.not.sign.in");

				if (error instanceof AxiosError) {
					message = error.response?.data?.detail || message;
				} else if (error instanceof Error) {
					message = error.message;
				}

				trackError("login", {
					message,
					code: "unknown",
					action: "login failed",
					username: formattedUsername,
				});
				trackError(AUTH_EVENT.LOGIN_FAILED, { ...authEventProps(AUTH_METHOD.PASSWORD, false), reason: "unknown" });
				showSnackbar(t("login.failed", { message }), "error");
			} finally {
				setIsFormLoading(false);
			}
		},
		[isBiometricLoading, track, trackError, showSnackbar, login, navigate, t, username],
	);

	const handleSetPassword = useCallback(
		async (e: React.SyntheticEvent<HTMLFormElement>): Promise<void> => {
			e.preventDefault();

			const formData = new FormData(e.currentTarget);
			const rawUsername = username.trim();
			const password = formData.get("password")?.toString();
			const passwordConfirm = formData.get("password-confirm")?.toString();

			if (!rawUsername || !password || !passwordConfirm) {
				showSnackbar(t("error.enter.credentials"), "warning");
				return;
			}

			if (password !== passwordConfirm) {
				showSnackbar(t("error.passwords.do.not.match"), "error");
				return;
			}

			const formattedUsername = rawUsername.includes("@") ? rawUsername.toLowerCase() : rawUsername;

			setIsFormLoading(true);

			try {
				await resetPasswordFromAppMutation.mutateAsync({
					username: formattedUsername,
					password,
				});
			} catch {
				// Error already handled in the mutation
			} finally {
				setIsFormLoading(false);
			}
		},
		[username, showSnackbar, t, resetPasswordFromAppMutation],
	);

	const isFormDisabled = isBiometricLoading || isFormLoading;

	const modeOptions: FlatSwitchOption<SignInMode>[] = [
		{ label: t("otp.mode.label"), value: "otp", "data-testid": "signin-mode-otp" },
		{ label: t("password.mode.label"), value: "password", "data-testid": "signin-mode-password" },
	];

	// Says what the chosen mode will actually DO. Without it the toggle only restyles a pill, and
	// people could not tell that "One time code" means a code is about to be sent to them.
	const modeSubtitle = mode === "otp" ? t("otp.mode.subtitle") : t("password.mode.subtitle");

	// The field accepts an email, a phone number OR a plain username, so a fixed envelope quietly
	// claimed "email only". Follow what is being typed instead, and stay neutral until it is decidable.
	const usernameKind = classifyUsernameInput(username);
	const UsernameIcon = usernameKind === "email" ? Email : usernameKind === "phone" ? Phone : Person;

	return (
		<SigninLayout title={t("sign.in")}>
			{step === "username" && (
				<>
					{Capacitor.isNativePlatform() && (
						<BiometricLoginButton
							showSeparator={false}
							onAutoLoginComplete={handleBiometricComplete}
							isFormLoading={isFormLoading}
							onBiometricLoadingChange={handleBiometricLoadingChange}
						/>
					)}

					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.375rem", mb: "0.25rem" }}>
						<SystemFlatSwitch
							data-testid="signin-mode-toggle"
							options={modeOptions}
							activeIndexOrValue={mode}
							onSwitch={(option) => setMode(option.value)}
							delayCallback={0}
							variant="brand"
							// Default is 10px on mobile — too small here; bump to standard body size.
							fontSizeOverrides={{ xs: "14px", lg: "14px", xl: "14px" }}
						/>
						{/* Announced, not just repainted: switching mode changes which secret you are about to
						    be asked for, and the pill alone conveys that visually only. */}
						<Typography data-testid="signin-mode-subtitle" role="status" aria-live="polite" variant="caption" sx={{ color: INK.MUTED, textAlign: "center" }}>
							{modeSubtitle}
						</Typography>
					</Box>

					<Box component="form" sx={{ display: "contents" }} onSubmit={handleUsernameContinue}>
						<SystemValidationTextField
							dataTestId="signin-username-field"
							label={t("username.label")}
							// Persistent, and legible rather than the border-tint default: this is the line that
							// tells people a phone number works, so it has to survive both focus and typing —
							// which the placeholder it replaced did not.
							defaultHelperText={t("username.helper")}
							helperTextColor={INK.MUTED}
							// The field is autofocused, so its focus ring is the first thing on screen. Restores
							// the brand outline the raw TextField had by theme default.
							focusBorderColor={STATUS_BADGE.BRAND_FALLBACK}
							required
							fullWidth
							value={username}
							onChange={onUsernameChange}
							disabled={isFormDisabled}
							autoFocus
							injectedProps={{ name: "username" }}
							inputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<UsernameIcon data-testid={`signin-username-icon-${usernameKind}`} sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
							}}
						/>
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button
								data-testid="signin-need-account-link"
								LinkComponent={({ href, ...rest }) => <RouterLink to={href} {...rest} />}
								variant="text"
								size="small"
								href="/create-account"
								disabled={isFormDisabled}
								sx={{
									width: "fit-content",
									height: "1.5rem",
									mb: "0.5rem",
									alignSelf: "end",
								}}
							>
								{t("need.account")}
							</Button>
						</Box>
						<SubmitButton data-testid="signin-continue-button" disabled={isFormDisabled}>
							{t("continue")}
						</SubmitButton>
					</Box>

					<LoginSeparator sx={{ my: "1rem" }}>{t("or.continue.with")}</LoginSeparator>

					<GoogleOAuthLoginButton />
					<AppleOAuthLoginButton />
				</>
			)}

			{step === "password" && (
				<Box component="form" sx={{ display: "contents" }} onSubmit={handleLogin}>
					<TextField
						data-testid="signin-password-username-field"
						placeholder={t("username.placeholder")}
						name="username"
						variant="outlined"
						size="small"
						value={username}
						disabled
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Email sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
							},
						}}
					/>
					<TextField
						data-testid="signin-password-field"
						placeholder={t("password")}
						name="password"
						type={showPassword ? "text" : "password"}
						variant="outlined"
						size="small"
						required
						autoFocus
						disabled={isFormDisabled}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Lock sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton data-testid="signin-password-visibility-toggle" onClick={() => setShowPassword((show) => !show)} disabled={isFormDisabled}>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<Box sx={{ display: "flex", justifyContent: "space-between" }}>
						<Button
							data-testid="signin-password-back-button"
							variant="text"
							size="small"
							onClick={() => setStep("username")}
							disabled={isFormDisabled}
							sx={{ width: "fit-content", height: "1.5rem", mb: "0.5rem" }}
						>
							{t("back")}
						</Button>
						<Button
							data-testid="signin-forgot-password"
							LinkComponent={({ href, ...rest }) => <RouterLink to={href} {...rest} />}
							variant="text"
							size="small"
							href="/forgot-password"
							disabled={isFormDisabled}
							sx={{
								width: "fit-content",
								height: "1.5rem",
								mb: "0.5rem",
								alignSelf: "end",
							}}
						>
							{t("forgot.password")}
						</Button>
					</Box>
					<SubmitButton data-testid="signin-login-button" disabled={isFormDisabled}>
						{t("sign.in")}
					</SubmitButton>
				</Box>
			)}

			{step === "set-password" && (
				<Box component="form" sx={{ display: "contents" }} onSubmit={handleSetPassword}>
					<TextField
						data-testid="signin-set-password-username-field"
						placeholder={t("username.placeholder")}
						name="username"
						variant="outlined"
						size="small"
						value={username}
						disabled
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Email sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
							},
						}}
					/>
					<TextField
						data-testid="signin-new-password-field"
						placeholder={t("password")}
						name="password"
						type={showPassword ? "text" : "password"}
						variant="outlined"
						size="small"
						required
						autoFocus
						disabled={isFormDisabled}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Lock sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton data-testid="signin-new-password-visibility-toggle" onClick={() => setShowPassword((show) => !show)} disabled={isFormDisabled}>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<TextField
						data-testid="signin-confirm-password-field"
						placeholder={t("confirm.password")}
						name="password-confirm"
						type={showPasswordConfirm ? "text" : "password"}
						variant="outlined"
						size="small"
						required
						disabled={isFormDisabled}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Lock sx={{ fill: (theme) => alpha(theme.palette.primary.dark, 0.5) }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											data-testid="signin-confirm-password-visibility-toggle"
											onClick={() => setShowPasswordConfirm((show) => !show)}
											disabled={isFormDisabled}
										>
											{showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							},
						}}
					/>
					<Box sx={{ display: "flex", justifyContent: "flex-start" }}>
						<Button
							data-testid="signin-set-password-back-button"
							variant="text"
							size="small"
							onClick={() => setStep("username")}
							disabled={isFormDisabled}
							sx={{ width: "fit-content", height: "1.5rem", mb: "0.5rem" }}
						>
							{t("back")}
						</Button>
					</Box>
					<SubmitButton data-testid="signin-set-password-button" disabled={isFormDisabled}>
						{t("set.password")}
					</SubmitButton>
				</Box>
			)}

			{step === "otp" && (
				<OtpStep
					username={username}
					onResend={() => sendOtp(username)}
					resendCooldown={resendCooldown}
					resendPending={sendOtpMutation.isPending}
					onUseAnotherMethod={() => setStep("username")}
					onLocked={() => setStep("locked")}
				/>
			)}

			{step === "locked" && <LockedStep onBack={() => setStep("username")} />}

			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					marginTop: "20px",
				}}
			>
				<HelpOutlined sx={{ fontSize: "0.8rem", color: "primary.main" }} />
				<Button
					data-testid="signin-contact-support-link"
					LinkComponent={({ href, ...rest }) => <RouterLink to={href} {...rest} />}
					variant="text"
					onClick={() => track("support clicked")}
					href={`mailto:${SUPPORT_EMAIL}`}
					sx={{
						height: "0.8em",
						fontSize: "0.8rem",
						marginLeft: "-10px",
					}}
				>
					{t("contact.support")}
				</Button>
			</Box>
		</SigninLayout>
	);
};
